import React from "react";
import Hero from "../components/Hero";
import ServiceSection from "../components/ServiceSection";
import ProjectSection from "../components/ProjectSection";
import ContactForm from "../components/ContactForm";
import PricingSection from "../components/PricingSection";
import SEO from "../components/SEO";

import {
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { keyframes } from "@emotion/react";

// Animated background keyframes (same motion, new colors via sx)
const blobMovement = keyframes`
  0% { transform: translate(0px, 0px) scale(1); }
  50% { transform: translate(40px, -30px) scale(1.05); }
  100% { transform: translate(0px, 0px) scale(1); }
`;

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://studio.wiyoutech.in/#website",
      "url": "https://studio.wiyoutech.in/",
      "name": "WiYou! Tech Studios",
      "description": "Digital technology and web design studio in Jaipur, Rajasthan, India.",
      "inLanguage": "en-US"
    },
    {
      "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
      "@id": "https://studio.wiyoutech.in/#organization",
      "name": "WiYou! Tech Studios",
      "url": "https://studio.wiyoutech.in/",
      "logo": "https://studio.wiyoutech.in/assets/logo.png",
      "description": "WiYou! Tech Studios is a Jaipur-based digital technology studio creating modern websites, web applications, custom software, digital menus, and QR menu solutions for businesses across India.",
      "telephone": "+917689919139",
      "email": "officialwiyou.team@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Jaipur",
        "addressRegion": "Rajasthan",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 26.9124,
        "longitude": 75.7873
      },
      "areaServed": ["Jaipur", "Rajasthan", "India"],
      "priceRange": "₹₹"
    }
  ]
};

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.default ?? "#ffffff",
        color: theme.palette.text.primary ?? "#000000",
        overflowX: "clip",
      }}
    >
      <SEO
        title="WiYou! Tech Studios | Web Development & Design Company in Jaipur"
        description="WiYou! Tech Studios is a Jaipur-based digital technology studio creating modern websites, web applications, custom software, digital menus, and QR menu solutions for businesses across India."
        keywords="web development company in Jaipur, website development company Jaipur, web design agency Jaipur, software development company Jaipur, QR menu design Jaipur, digital menu Jaipur, restaurant menu design Jaipur, custom web applications India, UI UX design Jaipur"
        canonicalUrl="https://studio.wiyoutech.in/"
        structuredData={HOME_SCHEMA}
      />
      {/* HERO SECTION */}
      <Box
        id="hero-wrapper"
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: isMobile ? "80vh" : "100vh",
          backgroundColor: "#f7f7fb00",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          mt: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-320px",
            left: isMobile ? "-460px" : "-320px",
            width: isMobile ? 820 : 1100,
            height: isMobile ? 820 : 1100,
            background: "radial-gradient(circle, #ffee9bff, transparent 70%)",
            filter: "blur(140px)",
            animation: `${blobMovement} 22s ease-in-out infinite`,
            opacity: isMobile ? 0.4 : 0.6,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: isMobile ? "-460px" : "-360px",
            width: isMobile ? 820 : 1150,
            height: isMobile ? 820 : 650,
            background: "radial-gradient(circle, rgb(255, 198, 128), transparent 70%)",
            filter: "blur(150px)",
            animation: `${blobMovement} 26s ease-in-out infinite`,
            opacity: isMobile ? 0.35 : 0.5,
            pointerEvents: "none",
          }}
        />

        <Box id="hero" sx={{ bgcolor: "transparent", pt: 10, pb: 6, width: "100%" }}>
          <Hero />
        </Box>
      </Box>

      {/* PAGE SECTIONS BELOW HERO */}
      <Box id="services" sx={{ bgcolor: "transparent" }}>
        <ServiceSection />
      </Box>

      <Box id="pricing" sx={{ bgcolor: "transparent", pt: 6, pb: -6 }}>
        <PricingSection />
      </Box>

      <Box id="projects" sx={{ bgcolor: "transparent", pt: -6, pb: -6 }}>
        <ProjectSection />
      </Box>

      <Box
        id="contact-section"
        sx={{
          bgcolor: "transparent",
        }}
      >
        <ContactForm />
      </Box>
    </Box>
  );
};

export default Home;
